import { inject } from "@angular/core";
import { signalStore, withState, withMethods, patchState } from "@ngrx/signals";
import { EMPTY, Observable, from, pipe, switchMap, tap } from "rxjs";
import { DxOdataStoreService } from "@shared/components/dx-data-grid/data-access";
import { rxMethod } from "@ngrx/signals/rxjs-interop";
import { ICertificate, ICredentials, INFSe } from "../entities/settings.interface";
import { SettingsService } from "../infra/settings.service";
import { ErrorStore } from "@application/errors/data-access";
import { AlertStore } from "@alert/data-access";
import { AuthStore } from '@auth/data-access';
import { APP_CONFIG } from "@application/config";
import { setPropError, setPropInit, setPropLoaded, setPropLoading, withReqState } from "@application/data-access";
import { HttpErrorResponse, HttpEventType, HttpResponse } from "@angular/common/http";
import { SelfUserStore, PermissionNumber } from '@domains/person/user/self/data';
import { tapResponse } from "@ngrx/operators";

const TEMP_AUTH_PERMISSION_GUARD_PATH = 'TEMPORARY_AUTH_MODAL.PERMISSION_GUARD';

export interface IIntegrations {
    Credentials: ICredentials;
    NFSe: INFSe;
    progressCertificate: number;
    apiKey: string | null;
}

export const IntegrationsInitialState: IIntegrations = {
    Credentials: null,
    NFSe: null,
    progressCertificate: 0,
    apiKey: null,
};

export const IntegrationsStore = signalStore(
    { providedIn: 'root' },
    withState(IntegrationsInitialState),
    withMethods((
        store,
        settingsService = inject(SettingsService),
        authStore = inject(AuthStore),
        errorStore = inject(ErrorStore),
        alertStore = inject(AlertStore),
        dxOdataStoreService = inject(DxOdataStoreService),
        url = inject(APP_CONFIG).apiUrl,
        selfUserStore = inject(SelfUserStore)
    ) => {
        const ensureAdjustPermission = () =>
            from(
                selfUserStore.ensurePermissions(
                    [PermissionNumber.AjusteAtualizar],
                    {
                        title: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.ADJUST_SETTINGS.TITLE`,
                        message: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.ADJUST_SETTINGS.MESSAGE`,
                    },
                ),
            );

        const guardAdjustPermission = <T>(requestKey: string, work: () => Observable<T>) =>
            ensureAdjustPermission().pipe(
                switchMap((hasPermission) => {
                    if (!hasPermission) {
                        patchState(store, setPropError(requestKey, new Error('ALERT.ERROR_401')));
                        return EMPTY;
                    }

                    return work();
                }),
            );

        return {
            getCredentials: rxMethod<void>(pipe(
                tap(() => {
                    patchState(store, { ...setPropLoading('getCredentials') })
                }),
                switchMap(() => settingsService.getCredentialsByTenantId(authStore.authData().TenantId).pipe(
                    tapResponse({
                        next: (response) => {
                            const { PrefectureUser, SecurityId } = response;
                            patchState(store, { Credentials: { PrefectureUser, SecurityId, PrefecturePassword: null }, ...setPropLoaded('getCredentials') });
                        },
                        error: (error: HttpErrorResponse) => {
                            errorStore.setHttpError(error);
                            patchState(store, { ...setPropError('getCredentials', error) });
                        }
                    })
                ))
            )),

            getNFSe: rxMethod<void>(pipe(
                tap(() => {
                    patchState(store, { ...setPropLoading('getNFSe') })
                }),
                switchMap(() => settingsService.getNFSeByTenantId(authStore.authData().TenantId).pipe(
                    tapResponse({
                        next: (response) => {
                            const { PersonId, TenantId, TributaryCode, Rps, Serie } = response || {};
                            patchState(store, { NFSe: { PersonId, TenantId, TributaryCode, Rps, Serie }, ...setPropLoaded('getNFSe') });
                        },
                        error: (error: HttpErrorResponse) => {
                            patchState(store, { ...setPropError('getNFSe', error) });
                            errorStore.setHttpError(error)
                        }
                    })
                ))
            )),

            createCertificate: rxMethod<Partial<ICertificate>>(pipe(
                tap(() => {
                    patchState(store, { ...setPropLoading('createCertificate') })
                }),
                switchMap((data) => guardAdjustPermission('createCertificate', () => settingsService.createCertificate({ ...data, OwnerSettingsId: authStore.authData().TenantId}).pipe(
                    tapResponse({
                        next: (response) => {
                            if (response.type === HttpEventType.UploadProgress) {
                            const progress = Math.round(
                                100 * (response.loaded / (response.total ?? 1))
                            );
                            patchState(store, { progressCertificate: progress });
                            return;
                            }
                            if (response instanceof HttpResponse) {
                            patchState(store, {
                                ...setPropLoaded('createCertificate'),
                            });
                            }
                        },
                        error: (error: HttpErrorResponse) => {
                            patchState(store, { ...setPropError('createCertificate', error) });
                            errorStore.setHttpError(error);
                        }
                    }),
                )))
            )),

            updateCertificate: rxMethod<Partial<ICertificate>>(pipe(
                tap(() => {
                    patchState(store, { ...setPropLoading('updateCertificate') })
                }),
                switchMap((data) => guardAdjustPermission('updateCertificate', () => settingsService.updateCertificate(data).pipe(
                    tapResponse({
                        next: () => {
                            patchState(store, { ...setPropLoaded('updateCertificate') })
                        },
                        error: (error: HttpErrorResponse) => {
                            errorStore.setHttpError(error);
                            patchState(store, { ...setPropError('updateCertificate', error) });
                        }
                    }),
                )))
            )),

            updateNFSe: rxMethod<Partial<INFSe>>(pipe(
                tap(() => {
                    patchState(store, { ...setPropLoading('updateNFSe') })
                }),
                switchMap((data) => guardAdjustPermission('updateNFSe', () => settingsService.updateNFSe(data).pipe(
                    tapResponse({
                        next: (NFSe) => {
                            patchState(store, { NFSe, ...setPropLoaded('updateNFSe') })
                        },
                        error: (error: HttpErrorResponse) => {
                            errorStore.setHttpError(error);
                            patchState(store, { ...setPropError('updateNFSe', error) });
                        }
                    }),
                )))
            )),

            updateCredentials: rxMethod<Partial<ICredentials>>(pipe(
                tap(() => {
                    patchState(store, { ...setPropLoading('updateCredentials') })
                }),
                switchMap((data) => guardAdjustPermission('updateCredentials', () => settingsService.updateCredentials(data).pipe(
                    tapResponse({
                        next: (credentials) => {
                            const { PrefectureUser, SecurityId } = credentials;
                            patchState(store, { Credentials: { PrefectureUser, SecurityId, PrefecturePassword: null }, ...setPropLoaded('updateCredentials') });
                        },
                        error: (error: HttpErrorResponse) => {
                            errorStore.setHttpError(error);
                            patchState(store, { ...setPropError('updateCredentials', error) });
                        }
                    }),
                )))
            )),

            getCertificates: () => {
                return dxOdataStoreService.fromOdata({
                    url: url.odata_account_service, metadata: 'CertificatesArchives', key: 'CertificateArchiveId', params: {
                        $orderby: 'IsActive desc',
                        $select: 'Name,ExpiresOn,IsActive,CertificateArchiveId,AddedOn'
                    }
                })
            },

            getApiKey: rxMethod<void>(pipe(
                tap(() => {
                    patchState(store, { ...setPropLoading('getApiKey') })
                }),
                switchMap(() => settingsService.getApiKey().pipe(
                    tapResponse({
                        next: (response) => {
                            patchState(store, { apiKey: response, ...setPropLoaded('getApiKey') });
                        },
                        error: (error: HttpErrorResponse) => {
                            errorStore.setHttpError(error);
                            patchState(store, { apiKey: null, ...setPropError('getApiKey', error) });
                        }
                    })
                ))
            )),
        }
    }),

    withReqState(
        'createCertificate',
        'saveIntegrations',
        'updateCertificate',
        'getNFSe',
        'getCredentials',
        'updateNFSe',
        'updateCredentials',
        'getApiKey',
    ),
);
